import React from "react";
import "./declare-html-microdata-attributes";
import { ImageInfoGalleryItem, Style, StyleType } from "gc-types";

import { centsToDollars, coreLibraryInitialised } from "../util";
import { Image } from "./Image";
import { microDataAttributeGenerator } from "./microDataAttribute";
import { CatalogueEvents } from "gc-types/generated/ipcBusEvents";
import { getRenderAsSubscription, isClearanceCatalogue } from "../../client/util";
import { getStyleItemAnalyticsData } from "../../client/Analytics";
import { CollectionDesignation } from "../interfaces";

export interface StyleItemProps {
  styleItem: Style;
  /*
   * lazyLoadAllImages: false => lazy-load the secondary images, but not the primary image
   * lazyLoadAllImages: true => lazy load every image for this style
   */
  lazyLoadAllImages: boolean;
  tag?: (styleItem: Style, collectionDesignation: CollectionDesignation) => string | undefined;
  position: number;
  camelCaseMicroData?: boolean;
  withStyleImageCarousels: boolean;
  hashUrl: string;
  collectionDesignation: CollectionDesignation;
}

const getTestClassName = (shouldRenderAsSubscription: boolean) => {
  return shouldRenderAsSubscription ? "t-is-subscription" : "t-is-on-demand";
};

const getUrl = (url: string, hashUrl: string, shouldRenderAsSubscription: boolean) => {
  if (shouldRenderAsSubscription) {
    if (hashUrl) {
      return `${url}?subscription#${hashUrl}`;
    }
    return `${url}?subscription`;
  }
  if (hashUrl) {
    return `${url}#${hashUrl}`;
  }
  return url;
};

const getDataProduct = ({ magento_product_id, style_code, name, try_on_allowed }: Style) =>
  JSON.stringify({
    id: magento_product_id,
    style: style_code,
    name: name,
    disallow_try_on: !try_on_allowed
  });

const renderGalleryItems = (
  { image_info }: Style,
  lazyLoadImage: boolean,
  camelCaseMicroData: boolean,
  withStyleImageCarousels: boolean
) => {
  if (!image_info) {
    return null;
  }
  return image_info.gallery.map((imageInfoGalleryItem: ImageInfoGalleryItem, index: number) => {
    const isPrimaryImage = index === 0;
    const label = image_info.label;
    if (withStyleImageCarousels || isPrimaryImage) {
      return (
        <Image
          {...{ imageInfoGalleryItem, lazyLoadImage, label }}
          key={index}
          {...(camelCaseMicroData ? { camelCaseMicroData } : {})}
        />
      );
    } else {
      return null;
    }
  });
};

/*
 * props.forSubscription determines whether the component is rendered as an on-demand style or a subscription style,
 * in cases where the item is both an on-demand style and a subscription style.
 * If the item is not both, forSubscription will not affect the rendering.
 */
export const StyleItem: React.FunctionComponent<StyleItemProps> = ({
  styleItem,
  lazyLoadAllImages,
  tag,
  position,
  camelCaseMicroData,
  withStyleImageCarousels,
  hashUrl,
  collectionDesignation
}) => {
  const {
    record_type,
    price_in_cents,
    url,
    magento_product_id,
    style_code,
    name,
    designer,
    retail_price_in_cents,
    discount_price_in_cents
  } = styleItem;

  const microdata = microDataAttributeGenerator(!!camelCaseMicroData);
  const isRentalStyle = record_type === StyleType.RentalUnit;
  const isDiscounted = !!discount_price_in_cents;
  const renderAsSubscription = getRenderAsSubscription(styleItem, collectionDesignation);

  const shouldRenderPrice = !!price_in_cents && !renderAsSubscription;
  const tagForStyle = tag && tag(styleItem, collectionDesignation);
  const urlLink = getUrl(url, hashUrl, renderAsSubscription);
  const testClass = getTestClassName(renderAsSubscription);

  const onStyleItemClick = () => {
    const isClearance = isClearanceCatalogue();
    const styleItemAnalyticsData = getStyleItemAnalyticsData(styleItem, collectionDesignation, isClearance, hashUrl);

    if (coreLibraryInitialised()) {
      (global as any).GlamCornerCore.bus.broadcast(CatalogueEvents.StyleItemClicked, styleItemAnalyticsData);
    }
  };

  return (
    <div
      className={`${testClass} t-product-item grid-item product-grid-item col-xs-6 col-md-4 col-lg-4`}
      data-product={getDataProduct(styleItem)}
      data-sku={style_code}
      data-zz=""
      {...microdata("itemProp", "itemListElement")}
      {...microdata("itemScope", true)}
      {...microdata("itemType", "http://schema.org/ListItem")}
    >
      <meta itemProp="position" content={`${position}`} />
      <div className="grid-item__image product-grid-item__image">
        <div className="u-aspect-controlled-content">
          <a data-testid="style-url" className="grid-item__link" href={urlLink} onClick={onStyleItemClick}>
            <div
              id={`gallery${magento_product_id}`}
              className={withStyleImageCarousels ? "grid-item__slides js-grid-item__slides owl-carousel" : ""}
            >
              {renderGalleryItems(styleItem, lazyLoadAllImages, !!camelCaseMicroData, withStyleImageCarousels)}
            </div>
          </a>
          <div
            className="heart-wrapper js-heart"
            dangerouslySetInnerHTML={{
              __html: `
            <svg class="nc-icon outline" xmlns="http://www.w3.org/2000/svg"
                 xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px" width="24px" height="24px"
                 viewBox="0 0 24 24">
              <g transform="translate(0, 0)">
                <path fill="none" stroke="#111111" stroke-width="2" stroke-linecap="square" stroke-miterlimit="10"
                      d="M17,1c-2.1,0-3.9,1.1-5,2.7 C10.9,2.1,9.1,1,7,1C3.7,1,1,3.7,1,7c0,6,11,15,11,15s11-9,11-15C23,3.7,20.3,1,17,1z"
                      stroke-linejoin="miter"></path>
              </g>
            </svg>
          `
            }}
          />
        </div>

        {tagForStyle && (
          <div className="product-grid-item__exclusive-tag">
            <div className="product-grid-item__exclusive-tag-inner">{tagForStyle.toUpperCase()}</div>
          </div>
        )}
      </div>

      <meta {...microdata("itemProp", "url")} content={urlLink} />

      <div className="product-grid-item__text product-grid-item__designer-name">
        <a className="grid-item__link" href={urlLink}>
          <span>{designer}</span>
        </a>
      </div>

      <div className="product-grid-item__text product-grid-item__product-name">
        <a className="grid-item__link" href={urlLink}>
          <span {...microdata("itemProp", "name")}>{name}</span>
        </a>
      </div>
      <div className="product-grid-item__text js-product-grid-item-prices">
        {shouldRenderPrice && (
          <span
            className={`product-grid-item__price ${isDiscounted ? "product-price-was" : ""}`}
            data-testid="style-item-price"
          >
            <span>$</span>
            <span className="js-price">{centsToDollars(price_in_cents)}</span>
            {isRentalStyle ? " rental" : " to buy"}
          </span>
        )}
        <span className="product-grid-item__price product-grid-item__price--retail sub-text">
          ${centsToDollars(retail_price_in_cents)} retail
        </span>
        {isDiscounted && shouldRenderPrice && (
          <span className="product-grid-item__price product-price-now">
            <span>$</span>
            <span className="js-price">{centsToDollars(discount_price_in_cents, 2)}</span>
            {isRentalStyle ? " rental" : " to buy"}
          </span>
        )}
      </div>
    </div>
  );
};
